import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { useHasPermission } from "./session-context"
import { ChildrenIcon, SearchIcon } from "./components/icons"
import { ChildrenPageSkeleton } from "./components/skeleton"
import { SponsorMatchModal } from "./components/sponsor-match-modal"
import { StatusTabs } from "./components/status-tabs"
import { ContentPagination } from "./content-components"
import { RowActionsMenu } from "./components/row-actions-menu"

type ChildStatus = "nominated" | "unverified" | "verified" | "sponsored" | "graduated"
const PAGE_SIZE = 10

interface ChildListItem {
  id: string
  code: string
  firstName: string
  lastName: string
  age: number
  classLevel: string
  school: string
  state: string
  status: ChildStatus
  sponsorName: string | null
  photoUrl: string | null
}

interface ChildDraftListItem {
  id: string
  code: string
  childName: string
  classLevel: string
  state: string
  updatedAt: string
}

interface StatusCounts {
  all: number
  nominated: number
  unverified: number
  verified: number
  sponsored: number
  graduated: number
}

const STATUS_COLORS: Record<ChildStatus, { bg: string; text: string }> = {
  nominated: { bg: "bg-orange-100", text: "text-orange-700" },
  unverified: { bg: "bg-red-100", text: "text-red-700" },
  verified: { bg: "bg-blue-100", text: "text-blue-700" },
  sponsored: { bg: "bg-green-100", text: "text-green-700" },
  graduated: { bg: "bg-gray-100", text: "text-gray-700" },
}



export function ChildrenPage() {
  const navigate = useNavigate()
  const canView = useHasPermission("children")
  const canMutate = useHasPermission("children_mutate")
  const [children, setChildren] = useState<ChildListItem[]>([])
  const [drafts, setDrafts] = useState<ChildDraftListItem[]>([])
  const [draftCount, setDraftCount] = useState(0)
  const [counts, setCounts] = useState<StatusCounts>({ all: 0, nominated: 0, unverified: 0, verified: 0, sponsored: 0, graduated: 0 })
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<ChildStatus | "all" | "drafts">("all")
  const [search, setSearch] = useState("")
  const [matchChildId, setMatchChildId] = useState<string | null>(null)

  const fetchChildren = async () => {
    try {
      if (activeTab === "drafts") {
        const res = await client.api.admin.children.drafts.$get()
        const body = await res.json()
        if (!res.ok) {
          setError(getErrorMessage(body))
          return
        }
        if ("data" in body) {
          setDrafts(body.data as ChildDraftListItem[])
          setDraftCount(body.data.length)
          setTotal(body.data.length)
        }
        return
      }

      const query: { status?: ChildStatus; search?: string; limit: string; offset: string } = {
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      }
      if (activeTab !== "all") query.status = activeTab
      if (search.trim()) query.search = search.trim()

      const res = await client.api.admin.children.$get({ query })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setChildren(body.data.items)
        setCounts(body.data.counts)
        setTotal(body.data.total)
      }
    } catch {
      setError("Failed to load children")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchChildren()
    } else {
      setLoading(false)
    }
  }, [activeTab, canView, page])

  useEffect(() => {
    if (canView && activeTab !== "drafts") {
      void client.api.admin.children.drafts.$get().then(async (res) => {
        if (!res.ok) return
        const body = await res.json()
        if ("data" in body) {
          setDrafts(body.data as ChildDraftListItem[])
          setDraftCount(body.data.length)
        }
      }).catch(() => undefined)
    }
  }, [canView, activeTab])

  useEffect(() => {
    setPage(1)
  }, [activeTab, search])

  // Debounced search
  useEffect(() => {
    if (!canView) return
    const timer = setTimeout(() => {
      void fetchChildren()
    }, 300)
    return () => clearTimeout(timer)
  }, [search])


  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Children</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view children.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <ChildrenPageSkeleton />
  }

  const tabs: { key: ChildStatus | "all" | "drafts"; label: string; count: number }[] = [
    { key: "all", label: "All", count: counts.all + draftCount },
    { key: "verified", label: "Verified", count: counts.verified },
    { key: "nominated", label: "Nominated", count: counts.nominated },
    { key: "unverified", label: "Unverified", count: counts.unverified },
    { key: "sponsored", label: "Sponsored", count: counts.sponsored },
    { key: "graduated", label: "Graduated", count: counts.graduated },
    { key: "drafts", label: "Drafts", count: draftCount },
  ]

  const showingDrafts = activeTab === "drafts"
  const showingAll = activeTab === "all"
  const visibleDrafts = drafts.filter((draft) => !search.trim() || draft.childName.toLowerCase().includes(search.trim().toLowerCase()))

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Children</h1>
          <p className="mt-1 text-sm text-ink/60">{counts.all} children enrolled · {draftCount} drafts</p>
        </div>
        {canMutate && (
          <button
            onClick={() => navigate("/admin/children/new")}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-forest-950 px-5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
          >
            <ChildrenIcon className="h-4 w-4" />
            Add child
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <StatusTabs
          tabs={tabs}
          activeKey={activeTab}
          onChange={setActiveTab}
          ariaLabel="Children status"
        />

        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
          <input
            type="text"
            placeholder={showingDrafts ? "Search drafts..." : "Search by name..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 w-64 rounded-full border border-gold-500 bg-cream-50 pl-10 pr-4 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>
      </div>

      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : showingDrafts ? visibleDrafts.length === 0 ? (
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">No saved drafts</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gold-500/50">
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Child</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Class</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">State</th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Last saved</th>
                  <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-ink/60">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold-500/30">
                {visibleDrafts.map((draft) => (
                  <tr key={draft.id} onClick={() => navigate(`/admin/children/new?draft=${draft.id}`)} className="cursor-pointer hover:bg-cream-50">
                    <td className="px-5 py-4">
                      <p className="text-sm font-medium text-ink">{draft.childName}</p>
                      <p className="text-xs text-ink/50">{draft.code}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">{draft.classLevel}</td>
                    <td className="px-5 py-4 text-sm text-ink">{draft.state}</td>
                    <td className="px-5 py-4 text-sm text-ink/60">{new Date(draft.updatedAt).toLocaleDateString("en-NG")}</td>
                    <td className="px-5 py-4 text-right text-sm font-medium text-forest-700">Continue</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (showingAll ? children.length + visibleDrafts.length : children.length) === 0 ? (
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">No children found</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gold-500/50">
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Child
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Class
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    School
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    State
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Status
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Sponsor
                  </th>
                  {canMutate && <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-ink/60">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gold-500/30">
                {showingAll && visibleDrafts.map((draft) => (
                  <tr key={draft.id} onClick={() => navigate(`/admin/children/new?draft=${draft.id}`)} className="cursor-pointer bg-gold-50/40 hover:bg-cream-50">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold-500 text-xs font-bold text-forest-950">D</span>
                        <div>
                          <p className="text-sm font-medium text-ink">{draft.childName}</p>
                          <p className="text-xs text-ink/50">{draft.code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">{draft.classLevel}</td>
                    <td className="px-5 py-4 text-sm text-ink">—</td>
                    <td className="px-5 py-4 text-sm text-ink">{draft.state}</td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center rounded-full bg-gold-100 px-2.5 py-0.5 text-xs font-medium text-gold-700">Draft</span>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink/40">—</td>
                    {canMutate && <td className="px-5 py-4 text-right text-sm font-medium text-forest-700">Continue</td>}
                  </tr>
                ))}
                {children.map((child) => (
                  <tr
                    key={child.id}
                    onClick={() => navigate(`/admin/children/${child.id}`)}
                    className="cursor-pointer hover:bg-cream-50"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {child.photoUrl ? (
                          <img
                            src={child.photoUrl}
                            alt={`${child.firstName} ${child.lastName}`}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-950 text-xs font-bold text-white">
                            {child.firstName[0]}
                            {child.lastName[0]}
                          </span>
                        )}
                        <div>
                          <p className="text-sm font-medium text-ink">
                            {child.firstName} {child.lastName}
                          </p>
                          <p className="text-xs text-ink/50">
                            Age {child.age} · {child.code}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">{child.classLevel}</td>
                    <td className="px-5 py-4 text-sm text-ink">{child.school || "No school assigned"}</td>
                    <td className="px-5 py-4 text-sm text-ink">{child.state}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                          STATUS_COLORS[child.status].bg
                        } ${STATUS_COLORS[child.status].text}`}
                      >
                        {child.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">
                      {child.sponsorName || <span className="text-ink/40">—</span>}
                    </td>
                    {canMutate && (
                      <td className="px-5 py-4 text-right">
                        <RowActionsMenu
                          label={`Actions for ${child.firstName} ${child.lastName}`}
                          items={[
                            {
                              label: "View record",
                              onClick: () => navigate(`/admin/children/${child.id}`),
                            },
                            ...((child.status === "verified" || child.status === "sponsored")
                              ? [{ label: "Match sponsor", onClick: () => setMatchChildId(child.id) }]
                              : []),
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ContentPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} disabled={loading} />

      <SponsorMatchModal
        open={matchChildId !== null}
        onClose={() => setMatchChildId(null)}
        childId={matchChildId ?? undefined}
        onMatched={() => {
          setMatchChildId(null)
          void fetchChildren()
        }}
      />
    </div>
  )
}
